import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, ZebraTable } from '@kawai/ui';
import {
  DEFAULT_FUEL_CATEGORIES,
  FUEL_CATEGORY_LABEL_MAX,
  FUEL_CATEGORY_MAX,
  FUEL_DISCOUNT_MAX,
  FUEL_DISCOUNT_MIN,
  FUEL_TYPES,
  KAKEURI_CATEGORY_ID,
  normalizeFuelSettings,
  type FuelCategory,
  type FuelDiscount,
  type FuelType,
} from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import { useFuelSettings } from '../lib/hooks/useFuelSettings.js';
import { callFuelSetSettings } from '../lib/api/fuelPriceActions.js';

const FUEL_LABELS: Readonly<Record<FuelType, string>> = {
  haioku: 'ハイオク',
  regular: 'レギュラー',
  keiyu: '軽油',
  touyu: '灯油',
};

/** 1 値引き分の入力（F-16b: 全燃料共通 / 燃料別の 2 モード）。値は文字列で保持。 */
interface DiscountInputs {
  common: boolean;
  commonValue: string;
  byFuel: Record<FuelType, string>;
}

interface PricingInputs {
  cashless: DiscountInputs;
  line: DiscountInputs;
  member: DiscountInputs;
  kakeuriBank: DiscountInputs;
  kakeuriDirectDebit: DiscountInputs;
}

type DiscountKey = keyof PricingInputs;

const DISCOUNT_KEYS: readonly DiscountKey[] = [
  'cashless',
  'line',
  'member',
  'kakeuriBank',
  'kakeuriDirectDebit',
] as const;

interface DiscountErrors {
  commonValue: string | undefined;
  byFuel: Record<FuelType, string | undefined>;
}

interface DiscountRowDef {
  key: DiscountKey;
  /** 行ヘッダに出す値引き名 */
  name: string;
  /** どの価格からの値引きか */
  source: string;
  /** 共通値 input の aria-label（F-16 初版からの名称を維持 = テスト互換） */
  commonAriaLabel: string;
  /** 燃料別 input の aria-label 接頭辞（`○○ ハイオク` になる） */
  fuelAriaPrefix: string;
}

const DISCOUNT_ROWS: readonly DiscountRowDef[] = [
  {
    key: 'cashless',
    name: 'キャッシュレス',
    source: '基準価格から引く',
    commonAriaLabel: 'キャッシュレス値引き',
    fuelAriaPrefix: 'キャッシュレス',
  },
  {
    key: 'line',
    name: 'LINE 会員',
    source: '基準価格から引く',
    commonAriaLabel: 'LINE 会員値引き',
    fuelAriaPrefix: 'LINE',
  },
  {
    key: 'member',
    name: '会員価格（価格一覧表の印刷のみ）',
    source: '基準価格から引く',
    commonAriaLabel: '会員価格値引き（価格一覧表の印刷）',
    fuelAriaPrefix: '会員価格',
  },
  {
    key: 'kakeuriBank',
    name: '掛売 振込',
    source: '掛売価格（一覧表の掛売区分）から引く',
    commonAriaLabel: '掛売 振込値引き',
    fuelAriaPrefix: '掛売 振込',
  },
  {
    key: 'kakeuriDirectDebit',
    name: '掛売 口座振替',
    source: '掛売価格（一覧表の掛売区分）から引く',
    commonAriaLabel: '掛売 口座振替値引き',
    fuelAriaPrefix: '掛売 口座振替',
  },
] as const;

function emptyDiscountInputs(): DiscountInputs {
  return {
    common: true,
    commonValue: '',
    byFuel: { haioku: '', regular: '', keiyu: '', touyu: '' },
  };
}

function discountToInputs(d: FuelDiscount): DiscountInputs {
  return {
    common: d.common,
    commonValue: String(d.commonValue),
    byFuel: {
      haioku: String(d.byFuel.haioku),
      regular: String(d.byFuel.regular),
      keiyu: String(d.byFuel.keiyu),
      touyu: String(d.byFuel.touyu),
    },
  };
}

function parseIntOrNaN(s: string): number {
  if (s.trim() === '') return Number.NaN;
  const n = Number(s);
  return Number.isInteger(n) ? n : Number.NaN;
}

function validateDiscount(s: string): string | undefined {
  const n = parseIntOrNaN(s);
  if (!Number.isFinite(n) || n < FUEL_DISCOUNT_MIN || n > FUEL_DISCOUNT_MAX) {
    return `${FUEL_DISCOUNT_MIN}〜${FUEL_DISCOUNT_MAX} の整数で入力してください`;
  }
  return undefined;
}

function validateCategoryLabel(label: string): string | undefined {
  const trimmed = label.trim();
  if (trimmed.length === 0) return '区分名を入力してください';
  if (trimmed.length > FUEL_CATEGORY_LABEL_MAX) {
    return `区分名は ${FUEL_CATEGORY_LABEL_MAX} 文字以内です`;
  }
  return undefined;
}

/** 既存 id と重複しない新規区分 id を生成する（`cat-` + 英数 6 桁）。 */
function newCategoryId(existing: ReadonlySet<string>): string {
  for (;;) {
    const id = `cat-${Math.random().toString(36).slice(2, 8)}`;
    if (!existing.has(id) && id.length === 10) return id;
  }
}

/**
 * F-16: 燃料アプリの設定ページ。
 *
 * 設定項目:
 *  - 区分: 一覧表（個別販売単価）の行（追加 / 名称変更 / 並べ替え / 削除）
 *  - 価格設定: 派生価格の値引き幅（キャッシュレス / LINE 燃料別 / 会員 / 掛売振込・口座振替）
 *
 * 保存は fuelSetSettings Callable 経由（tenant-admin 以上 / 履歴 + 監査ログ同時記録）。
 * 設定 doc が無いテナントはデフォルト（従来ハードコード互換）が初期表示される。
 *
 * S-136: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・表は縦横線なし・入力の面は灰）。
 * 入力を持つ表の行はゼブラにしない（灰面の上の灰の入力は同色面になる）。
 */

const CARD = 'flex flex-col gap-4 rounded-xl bg-white px-[30px] py-[26px]';
const CARD_TITLE = 'text-h-card font-semibold text-ink';
const NOTE = 'text-label text-ink-3';
const ALERT = 'rounded-lg bg-surface-2 px-[18px] py-3 text-label text-danger-ink';
const ERR = 'text-label text-danger-ink';
const OK = 'text-label text-success-ink';

export function FuelSettingsPage() {
  const { tenantId } = useAuth();
  const queryClient = useQueryClient();
  const fuelSettingsQ = useFuelSettings(tenantId);

  const [categories, setCategories] = useState<FuelCategory[]>([]);
  const [pricingInputs, setPricingInputs] = useState<PricingInputs>({
    cashless: emptyDiscountInputs(),
    line: emptyDiscountInputs(),
    member: emptyDiscountInputs(),
    kakeuriBank: emptyDiscountInputs(),
    kakeuriDirectDebit: emptyDiscountInputs(),
  });
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // 取得完了時に正規化した設定で初期化（doc 無し = デフォルト値）。ユーザー編集中は上書きしない。
  useEffect(() => {
    if (!fuelSettingsQ.isSuccess || hasUserEdited) return;
    const settings = normalizeFuelSettings(fuelSettingsQ.data);
    setCategories(settings.categories);
    setPricingInputs({
      cashless: discountToInputs(settings.pricing.cashless),
      line: discountToInputs(settings.pricing.line),
      member: discountToInputs(settings.pricing.member),
      kakeuriBank: discountToInputs(settings.pricing.kakeuriBank),
      kakeuriDirectDebit: discountToInputs(settings.pricing.kakeuriDirectDebit),
    });
  }, [fuelSettingsQ.isSuccess, fuelSettingsQ.data, hasUserEdited]);

  const categoryErrors = useMemo(
    () => categories.map((c) => validateCategoryLabel(c.label)),
    [categories],
  );
  const pricingErrors = useMemo(() => {
    const out = {} as Record<DiscountKey, DiscountErrors>;
    for (const key of DISCOUNT_KEYS) {
      const inputs = pricingInputs[key];
      // 非適用側の値も保存対象のため両モードとも検証する（disabled セルのエラーも表示される）
      const byFuel = {} as Record<FuelType, string | undefined>;
      for (const f of FUEL_TYPES) byFuel[f] = validateDiscount(inputs.byFuel[f]);
      out[key] = { commonValue: validateDiscount(inputs.commonValue), byFuel };
    }
    return out;
  }, [pricingInputs]);
  const hasAnyError =
    categoryErrors.some((e) => e !== undefined) ||
    DISCOUNT_KEYS.some(
      (key) =>
        pricingErrors[key].commonValue !== undefined ||
        FUEL_TYPES.some((f) => pricingErrors[key].byFuel[f] !== undefined),
    );

  function updateCategoryLabel(index: number, label: string) {
    setHasUserEdited(true);
    setCategories((prev) => prev.map((c, i) => (i === index ? { ...c, label } : c)));
  }

  function moveCategory(index: number, dir: -1 | 1) {
    const to = index + dir;
    if (to < 0 || to >= categories.length) return;
    setHasUserEdited(true);
    setCategories((prev) => {
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(to, 0, item as FuelCategory);
      return next;
    });
  }

  function removeCategory(index: number) {
    // レビュー #1/#12 + 2巡目 #4: 既定区分（aisin/kawai/kakeuri）は固定 id に外部の表示が
    // 紐づく（kakeuri = 派生行・印刷の掛売価格セクション / aisin・kawai = 客向け価格ページの
    // 直読みキー）ため、誤削除を confirm で防ぐ。id は UI から再入力できないため、
    // 復元は「既定区分を復元」ボタンで同じ id を再作成する。
    const target = categories[index];
    if (target && DEFAULT_FUEL_CATEGORIES.some((d) => d.id === target.id)) {
      const warn =
        target.id === KAKEURI_CATEGORY_ID
          ? '「掛売」区分を削除すると、振込 / 口座振替の派生行と印刷ページの掛売価格セクションも消えます。'
          : `既定区分「${target.label}」を削除すると、客向け価格ページの対応する表示が更新されなくなる可能性があります。`;
      if (
        !window.confirm(`${warn}削除しますか？（「既定区分を復元」ボタンで元に戻せます）`)
      ) {
        return;
      }
    }
    setHasUserEdited(true);
    setCategories((prev) => prev.filter((_, i) => i !== index));
  }

  function addCategory() {
    if (categories.length >= FUEL_CATEGORY_MAX) return;
    setHasUserEdited(true);
    setCategories((prev) => [
      ...prev,
      { id: newCategoryId(new Set(prev.map((c) => c.id))), label: '' },
    ]);
  }

  /** 既定区分の復元。固定 id（外部表示との紐づけ）は UI から再入力できないため専用アクションで再作成する。 */
  function restoreDefaultCategory(id: string, label: string) {
    if (categories.length >= FUEL_CATEGORY_MAX) return;
    setHasUserEdited(true);
    setCategories((prev) =>
      prev.some((c) => c.id === id) ? prev : [...prev, { id, label }],
    );
  }

  function updateCommonValue(key: DiscountKey, value: string) {
    setHasUserEdited(true);
    setPricingInputs((prev) => ({
      ...prev,
      [key]: { ...prev[key], commonValue: value },
    }));
  }

  function updateByFuel(key: DiscountKey, fuel: FuelType, value: string) {
    setHasUserEdited(true);
    setPricingInputs((prev) => ({
      ...prev,
      [key]: { ...prev[key], byFuel: { ...prev[key].byFuel, [fuel]: value } },
    }));
  }

  /**
   * 全燃料共通のオン/オフ切替。切替方向によらず**両側**の不正値を有効値でシード修復する:
   * まず不正な共通値を先頭の有効な燃料値で、次に不正な燃料セルを共通値で補完する。
   * 検証は両モードを常に対象とするため、disabled になった側に不正値が残ると編集不能な
   * エラーで保存が恒久ブロックされる（3巡目レビュー #1）。有効な値が 1 つも無い場合のみ
   * 編集可能な側にエラーが残る（＝ユーザーが直せる状態は維持される）。
   */
  function toggleCommon(key: DiscountKey, common: boolean) {
    setHasUserEdited(true);
    setPricingInputs((prev) => {
      const cur = prev[key];
      const isValid = (v: string) => validateDiscount(v) === undefined;
      let commonValue = cur.commonValue;
      const byFuel = { ...cur.byFuel };
      if (!isValid(commonValue)) {
        const seed = FUEL_TYPES.map((f) => byFuel[f]).find(isValid);
        if (seed !== undefined) commonValue = seed;
      }
      if (isValid(commonValue)) {
        for (const f of FUEL_TYPES) {
          if (!isValid(byFuel[f])) byFuel[f] = commonValue;
        }
      }
      return { ...prev, [key]: { common, commonValue, byFuel } };
    });
  }

  async function handleSubmit() {
    if (hasAnyError) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      const toPayload = (i: DiscountInputs): FuelDiscount => ({
        common: i.common,
        commonValue: parseIntOrNaN(i.commonValue),
        byFuel: {
          haioku: parseIntOrNaN(i.byFuel.haioku),
          regular: parseIntOrNaN(i.byFuel.regular),
          keiyu: parseIntOrNaN(i.byFuel.keiyu),
          touyu: parseIntOrNaN(i.byFuel.touyu),
        },
      });
      await callFuelSetSettings({
        categories: categories.map((c) => ({ id: c.id, label: c.label.trim() })),
        pricing: {
          cashless: toPayload(pricingInputs.cashless),
          line: toPayload(pricingInputs.line),
          member: toPayload(pricingInputs.member),
          kakeuriBank: toPayload(pricingInputs.kakeuriBank),
          kakeuriDirectDebit: toPayload(pricingInputs.kakeuriDirectDebit),
        },
      });
      setSubmitSuccess(true);
      // レビュー #6 + 2巡目 #2: hasUserEdited の解除（初期化 useEffect の再有効化）は
      // refetch **成功**時のみ。invalidateQueries は既定では refetch 失敗を握り潰して
      // resolve するため throwOnError で判定する。失敗時はフォームが編集値
      // （= 保存済みの値）を保持し続け、stale cache への巻き戻りを防ぐ。
      try {
        await queryClient.invalidateQueries(
          { queryKey: ['fuelSettings'] },
          { throwOnError: true },
        );
        setHasUserEdited(false);
      } catch {
        // 保存自体は成功。再取得失敗は次回の再取得（再訪・再試行）で解消する
      }
      await queryClient.invalidateQueries({ queryKey: ['fuelPriceHistory'] });
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '保存に失敗しました';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const hasKakeuri = categories.some((c) => c.id === KAKEURI_CATEGORY_ID);

  return (
    <div className="flex flex-col gap-6">
      <header>
        {/* S-10: Layout の sticky ヘッダがブランド <h1> を持つため、ページ見出しは <h2> */}
        <h2 className="text-h-page font-semibold text-ink">設定</h2>
        <p className="mt-1 text-body text-ink-2">
          一覧表の区分と派生価格の値引き幅を変更できます（tenant-admin のみ保存可能）。
          保存すると燃料価格ページと印刷ページに即時反映されます。
        </p>
      </header>

      {/* レビュー #3: 初回ロード完了（isSuccess）までフォームを描画しない。
          ロード前に編集できると初期化 useEffect が恒久スキップされ、保存で既存設定を
          空ベースの内容で全置換してしまうため（fuelSetSettings は全置換 set）。 */}
      {fuelSettingsQ.data === undefined ? (
        // 2巡目 #1: ゲートは isSuccess ではなく「初回未取得（data === undefined）」で判定する。
        // fetchFuelSettings は doc 無しを null で返すため undefined = 一度も取得できていない状態と
        // 一意に区別できる。isSuccess 判定だとバックグラウンド refetch の失敗（v5 は data 保持の
        // まま error 遷移）で編集中フォームごとエラー画面に置換されてしまう。
        <div className="flex flex-col items-start gap-3">
          {fuelSettingsQ.error ? (
            <>
              <p role="alert" className={ALERT}>
                設定の取得に失敗しました: {fuelSettingsQ.error.message}
              </p>
              <Button
                type="button"
                onClick={() => {
                  void fuelSettingsQ.refetch();
                }}
              >
                再試行
              </Button>
            </>
          ) : (
            <p role="status" className={NOTE}>
              読み込み中…
            </p>
          )}
        </div>
      ) : (
        <>
          {/* 初回取得後のバックグラウンド再取得失敗は、フォームを保ったまま非破壊バナーで通知 */}
          {fuelSettingsQ.error && (
            <p role="alert" className={ALERT}>
              設定の再取得に失敗しました（表示中の内容で編集を継続できます）:{' '}
              {fuelSettingsQ.error.message}
            </p>
          )}
          {/* ===== 区分 ===== */}
          <section aria-labelledby="categories-section" className={CARD}>
            <h2 id="categories-section" className={CARD_TITLE}>
              区分（一覧表の行）
            </h2>
            <p className="text-body text-ink-2">
              区分を削除すると、その区分の一覧表価格も次回の一覧表保存時に削除されます。
              {hasKakeuri &&
                '「掛売」区分を削除すると、振込 / 口座振替の派生行も表示されなくなります。'}
            </p>
            {categories.length === 0 && (
              <p className="text-body text-ink-2">
                区分がありません。「区分を追加」で作成してください。
              </p>
            )}
            <ul className="flex max-w-xl flex-col gap-2">
              {categories.map((cat, i) => (
                <li key={cat.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <Input
                      value={cat.label}
                      onChange={(e) => updateCategoryLabel(i, e.target.value)}
                      aria-label={`区分名 ${i + 1}`}
                      invalid={Boolean(categoryErrors[i])}
                      aria-invalid={Boolean(categoryErrors[i])}
                      placeholder="例: アイシン"
                      disabled={submitting}
                    />
                    {categoryErrors[i] && (
                      <p role="alert" className={`mt-1 ${ERR}`}>
                        {categoryErrors[i]}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => moveCategory(i, -1)}
                    disabled={submitting || i === 0}
                    aria-label={`区分 ${i + 1} を上へ`}
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => moveCategory(i, 1)}
                    disabled={submitting || i === categories.length - 1}
                    aria-label={`区分 ${i + 1} を下へ`}
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => removeCategory(i)}
                    disabled={submitting}
                    aria-label={`区分 ${i + 1} を削除`}
                  >
                    削除
                  </Button>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="soft"
                onClick={addCategory}
                disabled={submitting || categories.length >= FUEL_CATEGORY_MAX}
              >
                ＋ 区分を追加（最大 {FUEL_CATEGORY_MAX} 件）
              </Button>
              {/* 2巡目 #4: 既定区分は固定 id に外部表示が紐づき UI から id を再入力できないため、
                  欠けている既定区分ごとに復元ボタンを出す（kakeuri 専用だったものを一般化） */}
              {DEFAULT_FUEL_CATEGORIES.filter(
                (d) => !categories.some((c) => c.id === d.id),
              ).map((d) => (
                <Button
                  key={d.id}
                  type="button"
                  variant="soft"
                  onClick={() => restoreDefaultCategory(d.id, d.label)}
                  disabled={submitting || categories.length >= FUEL_CATEGORY_MAX}
                >
                  ＋ 既定区分「{d.label}」を復元
                  {d.id === KAKEURI_CATEGORY_ID && '（振込 / 口座振替の派生行つき）'}
                </Button>
              ))}
            </div>
          </section>

          {/* ===== 価格設定 ===== */}
          {/* ユーザーフィードバック（2026-07-15 ×2）: 横軸 = 燃料 / 縦軸 = 値引き種別のテーブル。
              各行にどの価格からの値引きかを明記し、「全燃料共通」列（チェック + 共通値）を
              ハイオクの左に置く。共通が無効のときだけ燃料別セルが編集可能になる。 */}
          <section aria-labelledby="pricing-section" className={CARD}>
            <h2 id="pricing-section" className={CARD_TITLE}>
              価格設定（値引き幅 / 円）
            </h2>
            <p className="text-body text-ink-2">
              「全燃料共通」にチェックすると共通の値引き幅を全燃料に適用します。チェックを外すと燃料ごとに設定できます。
              注意: 客向け価格ページ（LINE
              会員向け表示）の値引きは現在この設定に連動しません（別途対応予定）。
            </p>

            <div className="overflow-x-auto">
              <ZebraTable className="min-w-full" aria-label="価格設定">
                <thead>
                  <ZebraTable.HeadRow>
                    <ZebraTable.Th edge="first">値引き（対象の価格）</ZebraTable.Th>
                    <ZebraTable.Th>全燃料共通</ZebraTable.Th>
                    {FUEL_TYPES.map((f, i) => (
                      <ZebraTable.Th
                        key={f}
                        edge={i === FUEL_TYPES.length - 1 ? 'last' : undefined}
                      >
                        {FUEL_LABELS[f]}
                      </ZebraTable.Th>
                    ))}
                  </ZebraTable.HeadRow>
                </thead>
                <tbody>
                  {DISCOUNT_ROWS.map((row) => (
                    <DiscountSettingRow
                      key={row.key}
                      row={row}
                      inputs={pricingInputs[row.key]}
                      errors={pricingErrors[row.key]}
                      disabled={submitting}
                      onToggleCommon={(c) => toggleCommon(row.key, c)}
                      onChangeCommon={(v) => updateCommonValue(row.key, v)}
                      onChangeFuel={(f, v) => updateByFuel(row.key, f, v)}
                    />
                  ))}
                </tbody>
              </ZebraTable>
            </div>
          </section>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={submitting || hasAnyError || fuelSettingsQ.isLoading}
              onClick={() => {
                void handleSubmit();
              }}
            >
              {submitting ? '保存中…' : '設定を保存'}
            </Button>
            {hasAnyError && <span className={ERR}>入力にエラーがあります</span>}
            {submitError && (
              <span role="alert" className={ERR}>
                {submitError}
              </span>
            )}
            {submitSuccess && !submitError && (
              <span role="status" className={OK}>
                保存しました
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface DiscountCellProps {
  id: string;
  /** 視覚ラベルは行ヘッダが担うため、input には aria-label を付ける */
  ariaLabel: string;
  value: string;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
}

/** 価格設定テーブルのセル入力（固定幅の compact な v2 Input）。 */
function DiscountCell({
  id,
  ariaLabel,
  value,
  error,
  disabled,
  onChange,
}: DiscountCellProps) {
  const errorId = `${id}-error`;
  return (
    <>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={FUEL_DISCOUNT_MIN}
        max={FUEL_DISCOUNT_MAX}
        step={1}
        size="sm"
        fullWidth={false}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        invalid={Boolean(error)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className="ui-num w-24"
      />
      {error && (
        <p id={errorId} role="alert" className={`mt-1 ${ERR}`}>
          {error}
        </p>
      )}
    </>
  );
}

interface DiscountSettingRowProps {
  row: DiscountRowDef;
  inputs: DiscountInputs;
  errors: DiscountErrors;
  disabled: boolean;
  onToggleCommon: (common: boolean) => void;
  onChangeCommon: (value: string) => void;
  onChangeFuel: (fuel: FuelType, value: string) => void;
}

/**
 * 値引き 1 種類分の行。「全燃料共通」チェック + 共通値セル + 燃料別 4 セル。
 * 共通が有効なら燃料別セルを、無効なら共通値セルを disabled にする（非適用側も値は保持）。
 */
function DiscountSettingRow({
  row,
  inputs,
  errors,
  disabled,
  onToggleCommon,
  onChangeCommon,
  onChangeFuel,
}: DiscountSettingRowProps) {
  return (
    <tr>
      <th scope="row" className="px-3.5 py-2.5 text-left align-top">
        <span className="block font-medium text-ink">{row.name}</span>
        <span className="block text-note text-ink-3">{row.source}</span>
      </th>
      <ZebraTable.Td className="align-top">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={inputs.common}
            disabled={disabled}
            aria-label={`${row.name} 全燃料共通`}
            onChange={(e) => onToggleCommon(e.target.checked)}
            className="h-4 w-4 accent-brand"
          />
          <div>
            <DiscountCell
              id={`pricing-${row.key}-common`}
              ariaLabel={row.commonAriaLabel}
              value={inputs.commonValue}
              error={errors.commonValue}
              disabled={disabled || !inputs.common}
              onChange={onChangeCommon}
            />
          </div>
        </div>
      </ZebraTable.Td>
      {FUEL_TYPES.map((f) => (
        <ZebraTable.Td key={f} className="align-top">
          <DiscountCell
            id={`pricing-${row.key}-${f}`}
            ariaLabel={`${row.fuelAriaPrefix} ${FUEL_LABELS[f]}`}
            value={inputs.byFuel[f]}
            error={errors.byFuel[f]}
            disabled={disabled || inputs.common}
            onChange={(v) => onChangeFuel(f, v)}
          />
        </ZebraTable.Td>
      ))}
    </tr>
  );
}
